import { CAMINO } from '@somnhold/content';
import type { HermitRules, LitterRules } from '../types.ts';
import { base } from './litter-rules.ts';
import type { NaveLayout } from './ermita-layout.ts';

/**
 * The stretcher's rules for the cleric's chapter 4 (docs/sendas/clerigo.md, «La Ermita»; content CAMINO.hermit, vigil,
 * nave), from the nave's layout (battle/ermita-layout.ts): the fight's builder (campaign/sendas.ts) and the renderer's
 * scenes build them the same way. Lives in hit points worked out by the caller (seconds of D).
 */

const board = (L: NaveLayout): Omit<HermitRules, 'vigil' | 'nave'> => ({
  lantern: { ...L.lantern },
  altar: { ...L.altar },
  door: { ...L.door },
  mirrors: L.mirrors.map((m, i) => ({ q: m.q, r: m.r, stand: { ...(L.mirrorStand[i] as { x: number; y: number }) } })),
  splits: L.splits.map((m, i) => ({ q: m.q, r: m.r, stand: { ...(L.splitStand[i] as { x: number; y: number }) } })),
  ink: L.ink.map((p) => ({ ...p })),
  cells: L.cells.map((p) => ({ ...p })),
  atrium: { ...L.atrium },
  stand: CAMINO.hermit.stand,
  reach: CAMINO.hermit.reach,
});

const shell = (L: NaveLayout, kind: 'vigil' | 'nave'): Omit<LitterRules, 'hermit'> => ({
  ...base(),
  kind,
  way: [{ ...L.rest }],
  dens: [],
  foes: null,
  crows: null,
  limpet: null,
  mirrors: null,
});

/** The memory: Aud's last vigil, in the same nave sixty-two years before. */
export const vigilRules = (L: NaveLayout, life: { tongue: number; hush: number }): LitterRules => {
  const V = CAMINO.vigil;
  return {
    ...shell(L, 'vigil'),
    hermit: {
      ...board(L),
      vigil: {
        dawn: V.dawn,
        lamps: L.lamps.map((p) => ({ ...p })),
        every: V.every,
        light: V.light,
        in: V.in,
        glide: V.glide,
        tongue: { every: V.tongue.every, speed: V.tongue.speed, hp: life.tongue, snuff: V.tongue.snuff, fast: V.tongue.fast },
        wail: { first: V.wail.first, every: V.wail.every, warn: V.wail.warn, long: V.wail.long, hush: life.hush, mute: V.wail.mute },
        veil: { ...V.veil },
        dead: { first: V.dead.first, every: V.dead.every, n: [V.dead.n[0], V.dead.n[1]] },
        singer: V.singer,
      },
      nave: null,
    },
  };
};

/** The nave of the mirrors (the test): Tessa and Piet at the altar, her limpets' drain from the stretcher's own rule. */
export const naveRules = (L: NaveLayout, life: { limpet: number; singer: number; burn: number }): LitterRules => {
  const N = CAMINO.nave;
  return {
    ...shell(L, 'nave'),
    // (Only their drain and leap: they come out of the walls by battle/ermita.ts, not at bends.)
    limpet: { hp: life.limpet, speed: N.limpet.speed, drainBp: N.limpet.drainBp, heroBp: N.limpet.heroBp, burst: 0, near: 0, trickle: 0x7fffffff, cap: N.limpet.cap, leap: N.limpet.leap, bends: [] },
    hermit: {
      ...board(L),
      vigil: null,
      nave: {
        answer: { turns: [...L.answer.turns], splits: [...L.answer.splits] },
        start: [...L.start0],
        split0: [...L.split0],
        aud: L.aud,
        stone: { ...L.stone },
        take: N.take,
        hold: N.hold,
        burn: life.burn,
        limpet: { hp: life.limpet, speed: N.limpet.speed, drainBp: N.limpet.drainBp, heroBp: N.limpet.heroBp, trickle: N.limpet.trickle, cap: N.limpet.cap, first: N.limpet.first, mirrorBp: N.limpet.mirrorBp, leap: N.limpet.leap },
        singer: life.singer,
        dirge: { ...N.dirge },
        dead: { first: N.dead.first, every: N.dead.every, n: [N.dead.n[0], N.dead.n[1]], grow: N.dead.grow },
      },
    },
  };
};
